import { FC, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Grid,
    IconButton,
    InputAdornment,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    AutoAwesome,
    Delete,
    Edit,
    NoPhotography as NoImageIcon,
    Refresh,
    Search as SearchIcon,
    Visibility,
    VisibilityOff,
} from '@mui/icons-material';
import { AiLibraryGenerateDialog } from './AiLibraryGenerateDialog';
import {
    LibraryItemType,
    LibrarySection,
    LibraryTier,
    LIBRARY_TIER_LABELS,
} from '../../types/library';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    useDeleteLibrary,
    useLibraryList,
    useRefreshStats,
    useToggleVisibility,
} from '../../hooks/useLibrary';
import { useLibrarySections } from '../../hooks/useLibrarySection';

const LANGUAGE_OPTIONS = [
    { value: '', label: '전체' },
    { value: 'ko', label: '한국어' },
    { value: 'en', label: '영어' },
    { value: 'ja', label: '일본어' },
    { value: 'zh', label: '중국어' },
    { value: 'es', label: '스페인어' },
    { value: 'fr', label: '프랑스어' },
    { value: 'de', label: '독일어' },
];

const SORT_OPTIONS = [
    { value: 'id,desc', label: 'ID 내림차순' },
    { value: 'createdAt,desc', label: '최신등록순' },
    { value: 'downloadCount,desc', label: '다운로드순' },
    { value: 'ratingAverage,desc', label: '평점순' },
    { value: 'wordCount,desc', label: '단어수순' },
];

const sectionDisplayName = (s: LibrarySection): string => {
    const ko = s.translations.find((t) => t.lang === 'ko')?.displayName;
    return ko ?? s.code;
};

export const LibraryList: FC = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const initialCategorySectionId = (() => {
        const v = searchParams.get('categorySectionId');
        return v ? Number(v) : 0;
    })();
    const initialCurationSectionId = (() => {
        const v = searchParams.get('curationSectionId');
        return v ? Number(v) : 0;
    })();

    const [page, setPage] = useState(0);
    const [size, setSize] = useState(25);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [headLang, setHeadLang] = useState('');
    const [meaningLang, setMeaningLang] = useState('');
    const [categorySectionId, setCategorySectionId] = useState<number>(
        initialCategorySectionId
    );
    const [curationSectionId, setCurationSectionId] = useState<number>(
        initialCurationSectionId
    );
    const [itemType, setItemType] = useState<LibraryItemType | ''>('');
    const [isVisible, setIsVisible] = useState<string>('true');
    const [tier, setTier] = useState<LibraryTier | ''>('');
    const [sort, setSort] = useState<string>('id,desc');
    const [aiDialogOpen, setAiDialogOpen] = useState(false);

    // URL search params 반영
    useEffect(() => {
        const next = new URLSearchParams(searchParams);
        if (categorySectionId > 0) {
            next.set('categorySectionId', String(categorySectionId));
        } else {
            next.delete('categorySectionId');
        }
        if (curationSectionId > 0) {
            next.set('curationSectionId', String(curationSectionId));
        } else {
            next.delete('curationSectionId');
        }
        setSearchParams(next, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categorySectionId, curationSectionId]);

    const { data: categorySections = [] } = useLibrarySections('CATEGORY');
    const { data: curationSections = [] } = useLibrarySections('CURATION');

    const sortedCategorySections = useMemo(
        () =>
            [...categorySections].sort(
                (a, b) => a.sortOrder - b.sortOrder || a.id - b.id
            ),
        [categorySections]
    );
    const sortedCurationSections = useMemo(
        () =>
            [...curationSections].sort(
                (a, b) => a.sortOrder - b.sortOrder || a.id - b.id
            ),
        [curationSections]
    );

    // 데이터 조회
    const { data, isLoading, error, refetch } = useLibraryList({
        page: page + 1,
        size,
        q: search || undefined,
        headLang: headLang || undefined,
        meaningLang: meaningLang || undefined,
        categorySectionId:
            categorySectionId > 0 ? categorySectionId : undefined,
        curationSectionId:
            curationSectionId > 0 ? curationSectionId : undefined,
        itemType: itemType || undefined,
        isVisible:
            isVisible === 'true'
                ? true
                : isVisible === 'false'
                  ? false
                  : undefined,
        tier: tier || undefined,
        sort,
    });

    // Mutations
    const toggleVisibility = useToggleVisibility();
    const deleteLibrary = useDeleteLibrary();
    const refreshStats = useRefreshStats();

    const handleToggleVisibility = (id: number) => {
        toggleVisibility.mutate(id);
    };

    const handleDelete = (id: number) => {
        if (confirm('정말 삭제하시겠습니까?')) {
            deleteLibrary.mutate(id);
        }
    };

    const handleRefreshStats = (id: number) => {
        refreshStats.mutate(id);
    };

    if (isLoading) {
        return (
            <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                p={4}
                gap={2}
            >
                <CircularProgress />
                <Typography variant="body2" color="text.secondary">
                    라이브러리 목록 로딩 중...
                </Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box p={3}>
                <Alert severity="error">
                    {error.message || '데이터를 불러오는데 실패했습니다.'}
                </Alert>
            </Box>
        );
    }

    const libraries = data?.content || [];
    const totalCount = data?.totalCount || 0;

    return (
        <Box p={3}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h4" gutterBottom m={0}>
                    라이브러리 관리
                </Typography>
                <Box display="flex" gap={1}>
                    <IconButton onClick={() => refetch()} title="새로고침">
                        <Refresh />
                    </IconButton>
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={() => setAiDialogOpen(true)}
                        startIcon={<AutoAwesome />}
                        sx={{ height: '40px' }}
                    >
                        AI 생성
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => navigate('/library/create')}
                        sx={{ height: '40px' }}
                    >
                        추가
                    </Button>
                </Box>
            </Box>

            <Card sx={{ mb: 2 }}>
                <CardContent>
                    {/* 필터 영역 */}
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <TextField
                                label="검색"
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        setSearch(searchInput);
                                        setPage(0);
                                    }
                                }}
                                size="small"
                                fullWidth
                                placeholder="제목 검색 후 Enter"
                                slotProps={{
                                    input: {
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => {
                                                        setSearch(searchInput);
                                                        setPage(0);
                                                    }}
                                                    edge="end"
                                                >
                                                    <SearchIcon fontSize="small" />
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                            <TextField
                                select
                                label="타입"
                                value={itemType}
                                onChange={(e) =>
                                    setItemType(e.target.value as LibraryItemType | '')
                                }
                                size="small"
                                fullWidth
                            >
                                <MenuItem value="">전체</MenuItem>
                                <MenuItem value={LibraryItemType.GROUP}>그룹</MenuItem>
                                <MenuItem value={LibraryItemType.SINGLE}>단일</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                            <TextField
                                select
                                label="노출 여부"
                                value={isVisible}
                                onChange={(e) => setIsVisible(e.target.value)}
                                size="small"
                                fullWidth
                            >
                                <MenuItem value="">전체</MenuItem>
                                <MenuItem value="true">노출</MenuItem>
                                <MenuItem value="false">숨김</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                            <TextField
                                select
                                label="등급"
                                value={tier}
                                onChange={(e) => {
                                    setTier(e.target.value as LibraryTier | '');
                                    setPage(0);
                                }}
                                size="small"
                                fullWidth
                            >
                                <MenuItem value="">전체</MenuItem>
                                <MenuItem value={LibraryTier.FREE}>무료</MenuItem>
                                <MenuItem value={LibraryTier.PREMIUM}>유료</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                            <TextField
                                select
                                label="카테고리 섹션"
                                value={categorySectionId || ''}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    setCategorySectionId(v === '' ? 0 : Number(v));
                                    setPage(0);
                                }}
                                size="small"
                                fullWidth
                            >
                                <MenuItem value="">전체</MenuItem>
                                {sortedCategorySections.map((s) => (
                                    <MenuItem key={s.id} value={s.id}>
                                        {sectionDisplayName(s)}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                            <TextField
                                select
                                label="큐레이션 섹션"
                                value={curationSectionId || ''}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    setCurationSectionId(v === '' ? 0 : Number(v));
                                    setPage(0);
                                }}
                                size="small"
                                fullWidth
                            >
                                <MenuItem value="">전체</MenuItem>
                                {sortedCurationSections.map((s) => (
                                    <MenuItem key={s.id} value={s.id}>
                                        {sectionDisplayName(s)}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                            <TextField
                                select
                                label="정렬"
                                value={sort}
                                onChange={(e) => setSort(e.target.value)}
                                size="small"
                                fullWidth
                            >
                                {SORT_OPTIONS.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <Box display="flex" gap={1}>
                                <TextField
                                    select
                                    label="단어 언어"
                                    value={headLang}
                                    onChange={(e) => setHeadLang(e.target.value)}
                                    size="small"
                                    fullWidth
                                >
                                    {LANGUAGE_OPTIONS.map((option) => (
                                        <MenuItem
                                            key={option.value}
                                            value={option.value}
                                        >
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                                <TextField
                                    select
                                    label="의미 언어"
                                    value={meaningLang}
                                    onChange={(e) => setMeaningLang(e.target.value)}
                                    size="small"
                                    fullWidth
                                >
                                    {LANGUAGE_OPTIONS.map((option) => (
                                        <MenuItem
                                            key={option.value}
                                            value={option.value}
                                        >
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Box>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            <Card sx={{ mt: 2 }}>
                <CardContent sx={{ p: 0 }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>ID</TableCell>
                                <TableCell align="center">이미지</TableCell>
                                <TableCell>제목/카테고리</TableCell>
                                <TableCell>타입</TableCell>
                                <TableCell>언어</TableCell>
                                <TableCell>등급</TableCell>
                                <TableCell>상태(단어수/다운/평점)</TableCell>
                                <TableCell>노출</TableCell>
                                <TableCell>큐레이션</TableCell>
                                <TableCell>액션</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {libraries.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={10} align="center">
                                        <Typography color="text.secondary" py={4}>
                                            등록된 라이브러리가 없습니다.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                libraries.map((library) => (
                                    <TableRow
                                        key={library.id}
                                        hover
                                        sx={{ cursor: 'pointer' }}
                                        onClick={() => navigate(`/library/${library.id}`)}
                                    >
                                        <TableCell>{library.id}</TableCell>
                                        <TableCell align="center">
                                            {library.thumbnailUrl ? (
                                                <Box
                                                    component="img"
                                                    src={library.thumbnailUrl}
                                                    alt="thumbnail"
                                                    sx={{
                                                        width: 40,
                                                        height: 40,
                                                        objectFit: 'cover',
                                                        borderRadius: 1,
                                                        display: 'block',
                                                        margin: '0 auto',
                                                    }}
                                                />
                                            ) : (
                                                <Tooltip title="이미지 미등록">
                                                    <NoImageIcon color="disabled" />
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="bold">
                                                {library.title}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                            >
                                                {library.categorySection?.displayName ??
                                                    library.categorySection?.code ??
                                                    '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={
                                                    library.itemType ===
                                                    LibraryItemType.GROUP
                                                        ? '그룹'
                                                        : '단일'
                                                }
                                                color={
                                                    library.itemType ===
                                                    LibraryItemType.GROUP
                                                        ? 'primary'
                                                        : 'default'
                                                }
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {library.headLang} → {library.meaningLang}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={
                                                    LIBRARY_TIER_LABELS[
                                                        library.tier as LibraryTier
                                                    ]
                                                }
                                                color={
                                                    library.tier === LibraryTier.PREMIUM
                                                        ? 'warning'
                                                        : 'default'
                                                }
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption" display="block">
                                                단어:{' '}
                                                {library.wordCount.toLocaleString()}개
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                display="block"
                                                color="text.secondary"
                                            >
                                                ⬇️{' '}
                                                {library.downloadCount.toLocaleString()}{' '}
                                                / ⭐ {library.ratingAverage.toFixed(1)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell onClick={(e) => e.stopPropagation()}>
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    handleToggleVisibility(library.id)
                                                }
                                            >
                                                {library.isVisible ? (
                                                    <Visibility color="primary" />
                                                ) : (
                                                    <VisibilityOff />
                                                )}
                                            </IconButton>
                                        </TableCell>
                                        <TableCell>
                                            {library.curationSection ? (
                                                <Chip
                                                    label={
                                                        library.curationSection
                                                            .displayName ||
                                                        library.curationSection.code
                                                    }
                                                    color="secondary"
                                                    size="small"
                                                />
                                            ) : (
                                                <Typography
                                                    variant="caption"
                                                    color="text.disabled"
                                                >
                                                    -
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell onClick={(e) => e.stopPropagation()}>
                                            <Box display="flex" gap={0.5}>
                                                {library.itemType ===
                                                    LibraryItemType.GROUP && (
                                                    <IconButton
                                                        size="small"
                                                        onClick={() =>
                                                            handleRefreshStats(
                                                                library.id
                                                            )
                                                        }
                                                        title="통계 갱신"
                                                    >
                                                        <Refresh />
                                                    </IconButton>
                                                )}
                                                <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                        navigate(
                                                            `/library/${library.id}/edit`
                                                        )
                                                    }
                                                >
                                                    <Edit />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                        handleDelete(library.id)
                                                    }
                                                    color="error"
                                                >
                                                    <Delete />
                                                </IconButton>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>

                    <TablePagination
                        component="div"
                        count={totalCount}
                        page={page}
                        onPageChange={(_, newPage) => setPage(newPage)}
                        rowsPerPage={size}
                        onRowsPerPageChange={(e) => {
                            setSize(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        labelRowsPerPage="페이지당 행 수:"
                        labelDisplayedRows={({ from, to, count }) =>
                            `${from}-${to} / 전체 ${count !== -1 ? count : `${to} 이상`}`
                        }
                    />
                </CardContent>
            </Card>

            {/* AI 단어장 생성 다이얼로그 */}
            <AiLibraryGenerateDialog
                open={aiDialogOpen}
                onClose={() => setAiDialogOpen(false)}
            />
        </Box>
    );
};
